import React from 'react'
import {
  SimpleShowLayout,
  TextField,
  EmailField,
  ReferenceField,
  BooleanField,
  useRecordContext,
} from 'react-admin'
import { Chip, Typography, Box, Card, CardContent, Table, TableBody, TableRow, TableCell, useTheme } from '@mui/material'

import Show from '../common/views/Show'
import DateField from '../common/fields/DateField'
import LeadNotes from './LeadNotes'

const leadStatusChoices = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  meeting_scheduled: 'Meeting Scheduled',
  proposal_sent: 'Proposal Sent',
  converted: 'Converted',
  unqualified: 'Unqualified',
  lost: 'Lost',
  nurturing: 'Nurturing',
}

const leadSourceChoices = {
  typeform: 'Typeform',
  referral: 'Referral',
  linkedin: 'LinkedIn',
  user: 'Manual',
  other: 'Other',
}

const statusColors = {
  new: 'default',
  contacted: 'primary',
  qualified: 'info',
  meeting_scheduled: 'success',
  proposal_sent: 'warning',
  converted: 'success',
  unqualified: 'error',
  lost: 'error',
  nurturing: 'secondary',
}

const LeadTitle = ({ record }) => {
  return <span>{record ? `${record.full_name}` : ''}</span>
}

const StatusField = () => {
  const record = useRecordContext()

  if (!record) return null

  return (
    <Chip
      label={leadStatusChoices[record.status] || record.status}
      color={statusColors[record.status] || 'default'}
      size="small"
    />
  )
}

const SourceField = () => {
  const record = useRecordContext()

  if (!record) return null

  return <Typography variant="body2">{leadSourceChoices[record.source] || record.source}</Typography>
}

const LeadFormData = () => {
  const record = useRecordContext()
  const theme = useTheme()

  if (!record || !record.form_data) return null

  const formData = record.form_data
  const entries = Object.entries(formData).filter(([key]) => key !== '_history')

  if (entries.length === 0) return null

  const renderValue = (value) => {
    if (Array.isArray(value)) {
      return value.join(', ')
    }
    if (typeof value === 'object') {
      return JSON.stringify(value)
    }
    return String(value)
  }

  return (
    <Card sx={{ mt: 3, mx: 2 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Form Data
        </Typography>
        <Table size="small">
          <TableBody>
            {entries.map(([key, value]) => (
              <TableRow key={key}>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    width: '30%',
                    backgroundColor: theme.palette.mode === 'dark' ? 'action.hover' : 'action.disabled',
                  }}
                >
                  {key}
                </TableCell>
                <TableCell sx={{ wordBreak: 'break-word' }}>
                  {renderValue(value)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

const LeadShowContent = () => {
  return (
    <Box>
      <SimpleShowLayout>
        <TextField source="id" />
        <TextField source="first_name" label="First Name" />
        <TextField source="last_name" label="Last Name" />
        <EmailField source="email" />
        <TextField source="phone" />
        <TextField source="mobile" />
        <TextField source="company" />
        <TextField source="job_title" label="Job Title" />
        <TextField source="department" />
        <TextField source="website" />
        <TextField source="linkedin_url" label="LinkedIn Profile URL" />
        <StatusField label="Status" />
        <SourceField label="Source" />
        <TextField source="campaign" />
        <BooleanField source="marketing_consent" label="Marketing Consent" />
        <ReferenceField source="customer_id" reference="customers" label="Customer" allowEmpty>
          <TextField source="name" />
        </ReferenceField>
        <TextField source="project_description" label="Project Description" />
        <TextField source="project_reason" label="Project Reason" />
        <TextField source="project_location" label="Project Location" />
        <TextField source="project_timeline" label="Project Timeline" />
        <TextField source="project_budget" label="Project Budget" />
        <TextField source="expected_revenue" label="Expected Revenue" />
        <DateField source="created_at" />
        <DateField source="updated_at" />
      </SimpleShowLayout>
      <Box sx={{ mx: 2 }}>
        <LeadNotes />
      </Box>
      <LeadFormData />
    </Box>
  )
}

const LeadShow = (props) => (
  <Show title={<LeadTitle />} {...props}>
    <LeadShowContent />
  </Show>
)

export default LeadShow
